import Swiper from 'swiper'

document.addEventListener('DOMContentLoaded', function () {
  const firstLoadStorageKey = 'video-carousel-first-load-v1';
  let isFirstPageLoad = true;

  try {
    isFirstPageLoad = localStorage.getItem(firstLoadStorageKey) !== '1';
    if (isFirstPageLoad) {
      localStorage.setItem(firstLoadStorageKey, '1');
    }
  } catch (e) {
    // If storage is blocked, keep deterministic first-slide behavior.
    isFirstPageLoad = true;
  }

  function initCarousel(container) {
    const swiperContainer = container.querySelector('.swiper-container');
    const prev = container.querySelector('.nav.prev');
    const next = container.querySelector('.nav.next');
    let swiper = null;
    let currentVideo = null;
    let userSoundEnabled = false;
    let chainGeneration = 0;
    let chainOffHandler = null;

    function getSlides() {
      return Array.from(container.querySelectorAll('.swiper-slide'));
    }

    function getVisibleSlides() {
      const viewport = swiperContainer.getBoundingClientRect();
      return getSlides()
        .filter((slide) => {
          const rect = slide.getBoundingClientRect();
          if (!rect.width || !rect.height) return false;
          const overlap = Math.min(rect.right, viewport.right) - Math.max(rect.left, viewport.left);
          return overlap > rect.width * 0.25;
        })
        .sort((leftSlide, rightSlide) => {
          return leftSlide.getBoundingClientRect().left - rightSlide.getBoundingClientRect().left;
        });
    }

    function getClickedSlideDiff(clickedSlide) {
      const visibleSlides = getVisibleSlides();
      if (!visibleSlides.length) return 0;

      const viewport = swiperContainer.getBoundingClientRect();
      const viewportCenter = viewport.left + (viewport.width / 2);
      const clickedIndex = visibleSlides.indexOf(clickedSlide);
      if (clickedIndex < 0) return 0;

      let activeIndex = -1;
      let minDistance = Infinity;

      visibleSlides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const slideCenter = rect.left + (rect.width / 2);
        const distance = Math.abs(slideCenter - viewportCenter);
        if (distance < minDistance) {
          minDistance = distance;
          activeIndex = index;
        }
      });

      if (activeIndex < 0) return 0;
      return clickedIndex - activeIndex;
    }

    // Move by `diff` slides through Swiper's absolute slide API so navigation lands on
    // the exact physical slide that was clicked or swiped to, including loop clones.
    function cancelActiveChain() {
      if (swiper && chainOffHandler) {
        try { swiper.off('slideChangeTransitionEnd', chainOffHandler); } catch (err) {}
      }
      chainOffHandler = null;
    }

    function getCurrentSlideIndex() {
      if (!swiper) return 0;
      if (typeof swiper.activeIndex === 'number' && !Number.isNaN(swiper.activeIndex)) {
        return swiper.activeIndex;
      }
      return 0;
    }

    function getSlidePhysicalIndex(slide) {
      if (!swiper || !slide || !swiper.slides) return -1;
      const slides = Array.prototype.slice.call(swiper.slides);
      return slides.indexOf(slide);
    }

    function getSlidesCount() {
      return swiper && swiper.slides ? swiper.slides.length : 0;
    }

    function normalizePhysicalIndex(index) {
      const count = getSlidesCount();
      if (!count) return 0;
      return ((Number(index) % count) + count) % count;
    }

    // Navigate by diff steps using slideNext/slidePrev so Swiper handles loop
    // boundaries correctly. Intermediate steps use speed=0 (invisible), only
    // the final step is animated.
    function slideByDiff(diff) {
      if (!swiper || !diff) return;
      cancelActiveChain();
      chainGeneration++;
      const direction = diff > 0 ? 1 : -1;
      const steps = Math.abs(diff);
      const animatedSpeed = Number(swiper.params ? swiper.params.speed : 220) || 220;
      // Instant intermediate hops (no animation = user doesn't see them)
      for (let i = 0; i < steps - 1; i++) {
        try {
          if (direction > 0) swiper.slideNext(0, false);
          else swiper.slidePrev(0, false);
        } catch (err) {}
      }
      // Final animated step
      try {
        if (direction > 0) swiper.slideNext(animatedSpeed, true);
        else swiper.slidePrev(animatedSpeed, true);
      } catch (err) {}
    }

    function getCenterOffset(swiper) {
      const slidesPerView = Number(swiper && swiper.params ? swiper.params.slidesPerView : 1) || 1;
      return Math.floor(slidesPerView / 2);
    }

    function getActiveSlide(swiper) {
      if (!swiper || !swiper.slides || !swiper.slides.length) return null;
      const centerIndex = Math.min(swiper.slides.length - 1, swiper.activeIndex + getCenterOffset(swiper));
      return swiper.slides[centerIndex] || swiperContainer.querySelector('.swiper-slide-active');
    }

    function update(swiper) {
      const slides = getSlides();
      const activeSlide = getActiveSlide(swiper);

      slides.forEach((it) => {
        const isActive = it === activeSlide;
        it.classList.toggle('active', isActive);
        const v = it.querySelector('video');
        if (!v) return;
        v.style.pointerEvents = isActive ? '' : 'none';
        try { v.tabIndex = isActive ? 0 : -1; } catch (e) {}
        if (isActive) { loadVideo(v); }
        else { v.pause(); }
      });

      if (activeSlide) {
        const activeVideo = activeSlide.querySelector('video');
        if (activeVideo) {
          activeVideo.muted = !userSoundEnabled;
          attachOverlayTo(activeSlide, activeVideo, currentVideo !== activeVideo);
        }
      }

      const activeIndex = slides.indexOf(activeSlide);
      const range = 2;
      for (let i = Math.max(0, activeIndex - range); i <= Math.min(slides.length - 1, activeIndex + range); i++) {
        const vid = slides[i] ? slides[i].querySelector('video') : null;
        if (vid) loadVideo(vid);
      }
    }

    function pauseInactiveVideos(swiper) {
      const activeSlide = getActiveSlide(swiper);
      getSlides().forEach((slide) => {
        if (slide === activeSlide) return;
        const video = slide.querySelector('video');
        if (video) {
          // Ensure inactive videos are fully reset to their initial state
          try { video.pause(); } catch (e) {}
          try { video.currentTime = 0; } catch (e) {}
          try { video.muted = true; } catch (e) {}
          // clear any pending auto-advance timer on the inactive video
          try { if (video._advanceTimer) { clearTimeout(video._advanceTimer); delete video._advanceTimer; } } catch(e) {}
          // remove any overlay handlers left on the video
          try {
            if (video._overlayHandlers) {
              if (video._overlayHandlers.loaded) video.removeEventListener('loadedmetadata', video._overlayHandlers.loaded);
              if (video._overlayHandlers.canplay) video.removeEventListener('loadeddata', video._overlayHandlers.canplay);
              if (video._overlayHandlers.canplay) video.removeEventListener('canplay', video._overlayHandlers.canplay);
              if (video._overlayHandlers.time) video.removeEventListener('timeupdate', video._overlayHandlers.time);
              if (video._overlayHandlers.volume) video.removeEventListener('volumechange', video._overlayHandlers.volume);
              if (video._overlayHandlers.click) video.removeEventListener('click', video._overlayHandlers.click);
              if (video._overlayHandlers.play) video.removeEventListener('play', video._overlayHandlers.play);
              if (video._overlayHandlers.pause) video.removeEventListener('pause', video._overlayHandlers.pause);
              if (video._overlayHandlers.ended) video.removeEventListener('ended', video._overlayHandlers.ended);
              delete video._overlayHandlers;
            }
          } catch(e) {}
        }
      });
    }

    // shared overlay controls (single overlay moved into the active item)
    const sharedOverlay = container.querySelector('.video-overlay');
    const sharedMute = sharedOverlay.querySelector('.mute-btn');
    const sharedMuteImg = sharedMute ? sharedMute.querySelector('img') : null;
    const sharedProgressWrap = sharedOverlay.querySelector('.progress-wrap');
    const sharedProgress = sharedOverlay.querySelector('progress.progress');
    const sharedThumb = sharedOverlay.querySelector('.progress-thumb');
    const sharedTime = sharedOverlay.querySelector('.time-remaining');

    // shared center controls (play/pause/restart/link) - single element moved into active item
    let sharedCenter = container.querySelector('.center-controls');
    if (!sharedCenter) {
      sharedCenter = document.createElement('div');
      sharedCenter.className = 'center-controls';
      sharedCenter.innerHTML = '' +
        '<button class="center-icon play" aria-hidden="true"><img src="/assets/src/assets/img/opif/play.svg" alt=""></button>' +
        '<button class="center-icon pause" aria-hidden="true" style="display:none;"><img src="/assets/src/assets/img/opif/pause.svg" alt=""></button>' +
        '<div class="end-controls" aria-hidden="true" style="display:none;">' +
          '<button class="center-restart-btn" aria-label="Restart"><img src="/assets/src/assets/img/opif/restart.svg" alt=""></button>' +
          '<button class="js-scroll-button center-link-btn">К фондам</button>' +
        '</div>';
      // keep it in container; it will be moved into the active item when needed
      container.appendChild(sharedCenter);
      // attach single click handlers that operate on the currentVideo reference
      try {
        const restartBtn = sharedCenter.querySelector('.center-restart-btn');
        const linkBtn = sharedCenter.querySelector('.center-link-btn');
        if (restartBtn) restartBtn.addEventListener('click', function(ev){ ev && ev.preventDefault(); if (!currentVideo) return; if (currentVideo._advanceTimer) { clearTimeout(currentVideo._advanceTimer); delete currentVideo._advanceTimer; } try { currentVideo.currentTime = 0; } catch(e){} currentVideo.play().catch(()=>{}); const ec = sharedCenter.querySelector('.end-controls'); if (ec) ec.style.display = 'none'; });

        // map video title (substring) to target URL with UTM
        const videoTargetMap = [
          { title: 'Как выбрать фонд за 1 минуту', url: '/opif-catalog?utm_source=site_dohod&utm_medium=unpaid&utm_campaign=zlata_opif&utm_content=video&utm_term=podborka_fondov#opif-catalog' },
          { title: 'Куда вложить деньги на три месяца', url: 'https://www.dohod.ru/individuals?buyfund=2010&utm_source=site_dohod&utm_medium=unpaid&utm_campaign=zlata_opif&utm_content=video&utm_term=podborka_fondov' },
          { title: 'Куда переложить вклад', url: 'https://www.dohod.ru/individuals?buyfund=1711,71&utm_source=site_dohod&utm_medium=unpaid&utm_campaign=zlata_opif&utm_content=video&utm_term=podborka_fondov' },
          { title: 'Портфель для агрессивного роста', url: 'https://www.dohod.ru/individuals?buyfund=5,70,69&utm_source=site_dohod&utm_medium=unpaid&utm_campaign=zlata_opif&utm_content=video&utm_term=podborka_fondov' },
          { title: 'Портфель для начинающих', url: 'https://www.dohod.ru/individuals?buyfund=71,1711,5&utm_source=site_dohod&utm_medium=unpaid&utm_campaign=zlata_opif&utm_content=video&utm_term=podborka_fondov' }
        ];

        function findTargetByTitle(title) {
          if (!title) return null;
          title = title.trim();
          for (let i = 0; i < videoTargetMap.length; i++) {
            if (title.indexOf(videoTargetMap[i].title) >= 0) return videoTargetMap[i].url;
          }
          return null;
        }

        function sendAnalyticsForVideo(title, targetUrl) {
          try {
            const params = { event_category: 'video', event_label: title || '', value: 0, destination: targetUrl || '' };
            if (typeof window.gtag === 'function') {
              try { window.gtag('event', 'video_link_click', params); } catch (e) {}
            }
            if (window.dataLayer && Array.isArray(window.dataLayer)) {
              try { window.dataLayer.push(Object.assign({ event: 'video_link_click' }, params)); } catch (e) {}
            }
          } catch (e) {}
        }

        if (linkBtn) linkBtn.addEventListener('click', function(ev){
          ev && ev.preventDefault();
          try {
            // Determine slide: prefer currentVideo's slide, fallback to Swiper's active slide or DOM
            let slide = null;
            if (currentVideo && currentVideo.closest) slide = currentVideo.closest('.swiper-slide');
            if (!slide) {
              try { slide = getActiveSlide(swiper); } catch (e) { slide = null; }
            }
            if (!slide) slide = container.querySelector('.swiper-slide.active') || container.querySelector('.swiper-slide');
            const titleEl = slide ? slide.querySelector('.video-title') : null;
            const title = titleEl ? (titleEl.textContent || '').trim() : '';
            const target = findTargetByTitle(title);
            if (target) {
              sendAnalyticsForVideo(title, target);
              // Try to parse the target to decide whether to pushState (only hash change)
              let parsed = null;
              try { parsed = new URL(target, window.location.href); } catch (e) { parsed = null; }

              const isSameOrigin = parsed ? (parsed.origin === window.location.origin) : (target.indexOf('/') === 0);

              if (isSameOrigin) {
                // If only the hash differs from current location, use pushState + optional scroll.
                // Otherwise perform a full navigation so the new page is loaded.
                let doPushOnly = false;
                try {
                  if (parsed) {
                    const cur = new URL(window.location.href);
                    if (parsed.pathname === cur.pathname && parsed.search === cur.search) doPushOnly = true;
                  }
                } catch (e) { doPushOnly = false; }

                if (doPushOnly) {
                  try { history.pushState({}, '', parsed ? parsed.href : target); } catch (e) {}
                  // Optionally scroll to anchor if present and safe
                  try {
                    const rawHash = parsed ? (parsed.hash ? parsed.hash.slice(1) : '') : (target.indexOf('#')>=0 ? target.split('#')[1] : '');
                    const hash = rawHash ? rawHash.split('?')[0] : '';
                    if (hash) {
                      let allowAnchor = true;
                      try {
                        const parsedCheck = parsed || new URL(target, window.location.href);
                        try {
                          if (parsedCheck.origin === 'https://www.dohod.ru' && (parsedCheck.pathname === '/' || parsedCheck.pathname === '')) {
                            allowAnchor = false;
                          }
                        } catch (ex) {}
                        for (const [k, v] of parsedCheck.searchParams.entries()) {
                          if (/^utm/i.test(k) && /https?:\/\//i.test(v)) {
                            allowAnchor = false;
                            break;
                          }
                        }
                      } catch (e) { /* ignore parse errors */ }
                      if (allowAnchor) document.getElementById(hash) && document.getElementById(hash).scrollIntoView();
                    }
                  } catch (e) {}
                } else {
                  // different path or query -> perform full navigation (same-origin)
                  // If target contains a hash pointing to an element that exists on the current page,
                  // prefer pushState + scroll instead of navigating to a (possibly non-existent) path.
                  try {
                    const rawHash = parsed ? (parsed.hash ? parsed.hash.slice(1) : '') : (target.indexOf('#')>=0 ? target.split('#')[1] : '');
                    const hash = rawHash ? rawHash.split('?')[0] : '';
                    if (hash && document.getElementById(hash)) {
                      try { history.pushState({}, '', parsed ? parsed.href : target); } catch (e) {}
                      try { document.getElementById(hash).scrollIntoView(); } catch (e) {}
                    } else {
                      try { window.location.href = parsed ? parsed.href : target; } catch (e) {}
                    }
                  } catch (e) { try { window.location.href = parsed ? parsed.href : target; } catch (e) {} }
                }
              } else {
                // external link: redirect
                try { window.location.href = target; } catch (e) {}
              }
            } else {
              // fallback: just set hash to opif-catalog and send analytics
              sendAnalyticsForVideo(title, '#opif-catalog');
              try { history.pushState({}, '', window.location.pathname + '?utm_source=site_dohod&utm_medium=unpaid&utm_campaign=zlata_opif&utm_content=video&utm_term=podborka_fondov#opif-catalog'); } catch (e) { window.location.hash = 'opif-catalog'; }
            }
          } catch (e) {}
        });
      } catch(e) {}
    }

    // Stop propagation of pointer/touch/mouse events from the overlay and its controls
    // so they are not interpreted as swipe/drag on the track.
    if (sharedOverlay) {
      const stopList = ['pointerdown','pointermove','pointerup','touchstart','touchmove','touchend','mousedown','mousemove','mouseup','click'];
      stopList.forEach(ev => sharedOverlay.addEventListener(ev, (e) => { e.stopPropagation(); }));
      if (sharedMute) stopList.forEach(ev => sharedMute.addEventListener(ev, (e) => { e.stopPropagation(); }));
      if (sharedProgressWrap) stopList.forEach(ev => sharedProgressWrap.addEventListener(ev, (e) => { e.stopPropagation(); }));
      // stop propagation from center controls too
      if (sharedCenter) stopList.forEach(ev => sharedCenter.addEventListener(ev, (e) => { e.stopPropagation(); }));
    }

    // Ensure clicks on links inside this carousel that will navigate to a different
    // page do not trigger smooth-scrolling animation before navigation.
    try {
      container.addEventListener('click', function (e) {
        try {
          const link = e.target && e.target.closest ? e.target.closest('a') : null;
          if (!link) return;
          const href = link.getAttribute('href') || link.dataset && link.dataset.href || '';
          if (!href) return;
          let url = null;
          try { url = new URL(href, window.location.href); } catch (err) { return; }
          // If the link will change origin, path or query -> consider navigation away
          const willNavigateAway = (url.origin !== window.location.origin) || (url.pathname !== window.location.pathname) || (url.search !== window.location.search);
          if (willNavigateAway) disableSmoothScrollTemporarily();
        } catch (err) {}
      });
    } catch (e) {}

    function fmt(t) {
      if (!isFinite(t)) return '00:00';
      t = Math.max(0, Math.floor(t));
      const h = Math.floor(t/3600), m = Math.floor((t%3600)/60), s = t%60;
      if (h>0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
      return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    }

    // lazy-load a video's src from data-src when needed
    function loadVideo(v) {
      if (!v) return;
      // ensure iOS inline playback and muted attribute presence for autoplay
      try {
        v.setAttribute && v.setAttribute('playsinline', '');
        v.setAttribute && v.setAttribute('webkit-playsinline', '');
        if (!userSoundEnabled) {
          v.setAttribute && v.setAttribute('muted', '');
          try { v.muted = true; } catch(e){}
        } else {
          try { v.removeAttribute && v.removeAttribute('muted'); } catch(e){}
          try { v.muted = false; } catch(e){}
        }
      } catch(e) {}
      const ds = v.getAttribute('data-src');
      if (!ds) return;
      if (v._lazyLoaded) return;
      // set src and trigger load
      v.src = ds;
      try { v.load(); } catch (e) {}
      v._lazyLoaded = true;
    }

    function playVideo(v, restart) {
      if (!v) return;
      if (restart) {
        try { v.currentTime = 0; } catch (e) {}
      }
      const playAttempt = v.play();
      if (playAttempt && typeof playAttempt.catch === 'function') {
        playAttempt.catch(() => {});
      }
    }

    function attachOverlayTo(item, v, restartOnAttach) {
      if (!sharedOverlay) return;
      // move overlay into the active item
      item.appendChild(sharedOverlay);
      // move center controls into active item as well
      if (sharedCenter) item.appendChild(sharedCenter);
      if (sharedCenter) sharedCenter.classList.add('show');
      sharedOverlay.style.display = 'flex';

      // remove handlers from previous video
      const prev = currentVideo;
      if (prev && prev._overlayHandlers) {
        if (prev._overlayHandlers.loaded) prev.removeEventListener('loadedmetadata', prev._overlayHandlers.loaded);
        if (prev._overlayHandlers.canplay) prev.removeEventListener('loadeddata', prev._overlayHandlers.canplay);
        if (prev._overlayHandlers.canplay) prev.removeEventListener('canplay', prev._overlayHandlers.canplay);
        if (prev._overlayHandlers.time) prev.removeEventListener('timeupdate', prev._overlayHandlers.time);
        if (prev._overlayHandlers.volume) prev.removeEventListener('volumechange', prev._overlayHandlers.volume);
        if (prev._overlayHandlers.click) prev.removeEventListener('click', prev._overlayHandlers.click);
        if (prev._overlayHandlers.play) prev.removeEventListener('play', prev._overlayHandlers.play);
        if (prev._overlayHandlers.pause) prev.removeEventListener('pause', prev._overlayHandlers.pause);
        if (prev._overlayHandlers.ended) prev.removeEventListener('ended', prev._overlayHandlers.ended);
        // clear any pending auto-advance timer on previous video
        try { if (prev._advanceTimer) { clearTimeout(prev._advanceTimer); delete prev._advanceTimer; } } catch(e) {}
        delete prev._overlayHandlers;
      }

      currentVideo = v;

      // make sure end controls are hidden initially
      const playBtn = sharedCenter.querySelector('.play');
      const pauseBtn = sharedCenter.querySelector('.pause');
      const endControls = sharedCenter.querySelector('.end-controls');
      if (playBtn) { playBtn.style.display = 'none'; }
      if (pauseBtn) { pauseBtn.style.display = 'none'; pauseBtn.style.opacity = '1'; }
      if (endControls) { endControls.style.display = 'none'; }

      // handlers update overlay UI
      const onLoaded = () => {
        if (sharedProgress) sharedProgress.max = Math.floor(v.duration) || 0;
        if (sharedProgress) sharedProgress.value = Math.floor(v.currentTime) || 0;
        if (sharedTime) sharedTime.textContent = fmt(v.duration - v.currentTime);
        // if requested, restart from beginning once metadata is available
        if (v._restartOnLoad) {
          playVideo(v, true);
          delete v._restartOnLoad;
        }
        updateThumbPosition();
      };
      const onCanPlay = () => {
        if (currentVideo === v && v.paused) playVideo(v, false);
      };
      const onTime = () => {
        if (sharedProgress && !isNaN(v.duration)) {
          sharedProgress.max = Math.floor(v.duration);
          sharedProgress.value = Math.floor(v.currentTime);
        }
        if (sharedTime && !isNaN(v.duration)) {
          sharedTime.textContent = fmt(v.duration - v.currentTime);
        }
        updateThumbPosition();
      };
      const onVolumeChange = () => {
        if (sharedMuteImg && currentVideo) sharedMuteImg.src = currentVideo.muted ? '/assets/src/assets/img/opif/no-volume.svg' : '/assets/src/assets/img/opif/volume.svg';
      };
      const onVideoClick = (e) => {
        e.stopPropagation();
        if (!v) return;
        // If there is a pending auto-advance (video ended recently), cancel it
        try {
          if (v._advanceTimer) { clearTimeout(v._advanceTimer); delete v._advanceTimer; }
          if (v._advanceScheduled) { delete v._advanceScheduled; }
          // hide end-controls if visible
          try { const ec = sharedCenter && sharedCenter.querySelector('.end-controls'); if (ec) ec.style.display = 'none'; } catch(e){}
        } catch(e) {}

        // Toggle playback only; do not change muted state on video click.
        if (v.paused) {
          v.play().catch(()=>{});
        } else {
          v.pause();
        }
      };

      // center control handlers: show play when paused, show pause then fade on play, show end controls on ended
      const showCenterPlay = () => {
        if (!sharedCenter) return;
        if (playBtn) { playBtn.style.display = 'inline-flex'; }
        if (pauseBtn) { pauseBtn.style.display = 'none'; pauseBtn.style.opacity = '1'; }
        if (endControls) { endControls.style.display = 'none'; }
        sharedCenter.classList.add('show');
      };
      const showCenterPauseThenFade = () => {
        if (!sharedCenter) return;
        if (playBtn) playBtn.style.display = 'none';
        if (pauseBtn) { pauseBtn.style.display = 'inline-flex'; pauseBtn.style.opacity = '1'; }
        if (endControls) endControls.style.display = 'none';
        sharedCenter.classList.add('show');
        // fade out after 1s
        setTimeout(() => {
          try { if (pauseBtn) pauseBtn.style.opacity = '0'; } catch(e){}
          // hide after transition
          setTimeout(()=>{ try{ if (pauseBtn) pauseBtn.style.display = 'none'; }catch(e){} }, 400);
        }, 1000);
      };

      let advanceTimer = null;
      const onEnded = () => {
        if (!sharedCenter) return;
        if (playBtn) playBtn.style.display = 'none';
        if (pauseBtn) { pauseBtn.style.display = 'none'; }
        if (endControls) endControls.style.display = 'flex';
        sharedCenter.classList.add('show');
        // start auto-advance after 3s unless restarted
        if (advanceTimer) clearTimeout(advanceTimer);
        advanceTimer = setTimeout(() => {
          try {
            if (swiper && typeof swiper.slideNext === 'function') swiper.slideNext();
          } catch (e) {}
        }, 4000);
        // store timer on video so we can cancel when needed
        v._advanceTimer = advanceTimer;
      };

      const onPlayShowPause = () => { showCenterPauseThenFade(); };
      const onPauseShowPlay = () => { showCenterPlay(); };

      const onRestartClick = (ev) => {
        ev && ev.preventDefault();
        if (!v) return;
        // cancel pending auto-advance
        if (v._advanceTimer) { clearTimeout(v._advanceTimer); delete v._advanceTimer; }
        // restart video
        try { v.currentTime = 0; } catch (e) {}
        v.play().catch(()=>{});
        if (endControls) endControls.style.display = 'none';
      };

      // center control clicks handled by sharedCenter listeners bound at creation

      v._overlayHandlers = { loaded: onLoaded, canplay: onCanPlay, time: onTime, volume: onVolumeChange, click: onVideoClick, play: onPlayShowPause, pause: onPauseShowPlay, ended: onEnded };
      v.addEventListener('loadedmetadata', onLoaded);
      v.addEventListener('loadeddata', onCanPlay);
      v.addEventListener('canplay', onCanPlay);
      v.addEventListener('timeupdate', onTime);
      v.addEventListener('volumechange', onVolumeChange);
      v.addEventListener('click', onVideoClick);
      v.addEventListener('play', onPlayShowPause);
      v.addEventListener('pause', onPauseShowPlay);
      v.addEventListener('ended', onEnded);

      // ensure src is set and schedule restart if requested
      if (restartOnAttach) {
        v._restartOnLoad = true;
      }
      loadVideo(v);
      // if not a restart-for-swipe, try to play immediately (muted autoplay allowed)
      if (!restartOnAttach) {
        playVideo(v, false);
      }

      // update mute icon according to current video's muted state
      if (sharedMuteImg) {
        sharedMuteImg.src = currentVideo.muted ? '/assets/src/assets/img/opif/no-volume.svg' : '/assets/src/assets/img/opif/volume.svg';
      }

      // if video is paused on attach, show play icon
      if (v.paused) {
        try { if (sharedCenter) { const pb = sharedCenter.querySelector('.play'); if (pb) pb.style.display = 'inline-flex'; sharedCenter.classList.add('show'); } } catch(e) {}
      }

      onLoaded();
    }

    // Equalize heights of all .video-title elements within this carousel
    function syncTitleHeights() {
      const titles = Array.from(container.querySelectorAll('.video-title'));
      if (!titles.length) return;
      // reset to auto to measure natural heights
      titles.forEach(t => { t.style.height = 'auto'; });
      let max = 0;
      titles.forEach(t => { max = Math.max(max, t.offsetHeight); });
      if (max > 0) titles.forEach(t => { t.style.height = max + 'px'; });
    }

    // mute toggle (affects currentVideo)
    sharedMute.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!currentVideo) return;
      currentVideo.muted = !currentVideo.muted;
      userSoundEnabled = !currentVideo.muted;
      // swap icon
      if (sharedMuteImg) sharedMuteImg.src = currentVideo.muted ? 'assets/src/assets/img/opif/no-volume.svg' : 'assets/src/assets/img/opif/volume.svg';
      sharedMute.setAttribute('aria-pressed', String(!currentVideo.muted));
    });

    // Attach manual navigation handlers so clicks behave exactly like swipes/clicks.
    try {
      if (next) {
        next.addEventListener('click', function (e) {
          try { e && e.preventDefault && e.preventDefault(); } catch (err) {}
          try { e && e.stopPropagation && e.stopPropagation(); } catch (err) {}
          try { chainGeneration++; cancelActiveChain(); } catch (err) {}
          try { if (!swiper) return; swiper.slideNext(); } catch (err) {}
        });
      }
      if (prev) {
        prev.addEventListener('click', function (e) {
          try { e && e.preventDefault && e.preventDefault(); } catch (err) {}
          try { e && e.stopPropagation && e.stopPropagation(); } catch (err) {}
          try { chainGeneration++; cancelActiveChain(); } catch (err) {}
          try { if (!swiper) return; swiper.slidePrev(); } catch (err) {}
        });
      }
    } catch (err) {}

    // seeking controls use currentVideo reference
    let seeking = false;
    let wasPlayingBeforeSeek = false;
    function updateThumbPosition() {
      if (!sharedThumb || !sharedProgress) return;
      const max = Number(sharedProgress.max) || 0;
      const val = Number(sharedProgress.value) || 0;
      const pct = max > 0 ? (val / max) * 100 : 0;
      sharedThumb.style.left = pct + '%';
    }

    function applySeekValue(value, commitToVideo) {
      const nextValue = Number(value) || 0;
      if (sharedProgress) sharedProgress.value = nextValue;
      if (sharedTime) sharedTime.textContent = fmt((sharedProgress.max || 0) - nextValue);
      updateThumbPosition();

      if (!commitToVideo || !currentVideo) return;
      // User seek should win over deferred "restart from zero" behavior.
      if (currentVideo._restartOnLoad) delete currentVideo._restartOnLoad;
      const duration = Number(currentVideo.duration) || Number(sharedProgress.max) || 0;
      const clamped = Math.min(duration, Math.max(0, nextValue));
      try { currentVideo.currentTime = clamped; } catch (e) {}
    }

    if (sharedProgress) {
      // update thumb on time/progress changes
      sharedProgress.addEventListener('change', () => updateThumbPosition());
      // allow clicking/dragging on the wrap to seek
      if (sharedProgressWrap) {
        const getValueFromEvent = (e) => {
          const rect = sharedProgressWrap.getBoundingClientRect();
          const x = (e.touches && e.touches[0]) ? e.touches[0].clientX : e.clientX;
          const pct = Math.min(1, Math.max(0, (x - rect.left) / rect.width));
          const max = Number(sharedProgress.max) || Number(currentVideo && currentVideo.duration) || 0;
          return Math.round(pct * max);
        };

        const onPointerDown = (e) => {
          try { e.preventDefault && e.preventDefault(); } catch (err) {}
          applySeekValue(getValueFromEvent(e), true);
        };
        sharedProgressWrap.addEventListener('pointerdown', onPointerDown);
        sharedProgressWrap.addEventListener('touchstart', onPointerDown, {passive:true});
      }
    }

    if (typeof Swiper !== 'function') {
      console.error('Swiper 5.4.5 is not available');
      return;
    }

    // Ensure there are at least `desiredLarge` slides by generating clones
    const wrapper = swiperContainer.querySelector('.swiper-wrapper');
    const desiredLarge = 5;
    // remove previously generated clones if any
    Array.from(wrapper.querySelectorAll('[data-generated-clone]')).forEach(n => n.remove());
    const originalSlides = Array.from(wrapper.querySelectorAll('.swiper-slide')).filter(n => !n.hasAttribute('data-generated-clone'));
    if (originalSlides.length > 0 && originalSlides.length < desiredLarge) {
      let i = 0;
      while (wrapper.querySelectorAll('.swiper-slide').length < desiredLarge) {
        const src = originalSlides[i % originalSlides.length];
        const clone = src.cloneNode(true);
        clone.setAttribute('data-generated-clone', 'true');
        // ensure cloned videos don't carry loaded src/state
        const cv = clone.querySelector('video');
        if (cv) {
          try { cv.pause(); } catch (e) {}
          cv.removeAttribute('src');
          cv._lazyLoaded = false;
        }
        wrapper.appendChild(clone);
        i++;
      }
    }

    // allow clicking on an inactive slide: compute difference from active (-2..2) and shift by that amount
    try {
      wrapper.addEventListener('click', function (e) {
        try {
          const slide = e.target && e.target.closest ? e.target.closest('.swiper-slide') : null;
          if (!slide) return;

          const diff = getClickedSlideDiff(slide);
          const clickedPhysicalIndex = getSlidePhysicalIndex(slide);
          const activeSlide = swiper && swiper.slides ? swiper.slides[swiper.activeIndex] : null;
          const activePhysicalIndex = activeSlide ? getSlidePhysicalIndex(activeSlide) : -1;

          if (clickedPhysicalIndex < 0) return;
          // diff===0 means the user clicked the already-active center slide
          if (diff === 0) return;

          try { e.preventDefault && e.preventDefault(); } catch (err) {}
          try { e.stopPropagation && e.stopPropagation(); } catch (err) {}

              try {
                if (!swiper) return;
                chainGeneration++; cancelActiveChain();
                slideByDiff(diff);
              } catch (err) {}
        } catch (err) {}
      });
    } catch (e) {}

    // Keep lazy-load by default. After first interaction, preload only N nearest slides.
    let _eagerLoaded = false;
    const PRELOAD_RADIUS = 3; // preload current +/- PRELOAD_RADIUS slides

    function preloadNearbyVideos(radius) {
      if (_eagerLoaded) return;
      _eagerLoaded = true;
      radius = Number.isFinite(radius) ? radius : PRELOAD_RADIUS;
      try {
        const slides = Array.from(wrapper.querySelectorAll('.swiper-slide'));
        // determine center index: prefer swiper if available
        let center = 0;
        try {
          if (swiper && swiper.slides && typeof swiper.activeIndex === 'number') {
            center = Math.min(slides.length - 1, Math.max(0, swiper.activeIndex + getCenterOffset(swiper)));
          } else {
            // fallback: find first slide with class 'active' or 0
            const act = slides.findIndex(s => s.classList.contains('active'));
            center = act >= 0 ? act : 0;
          }
        } catch (e) { center = 0; }

        for (let offset = -radius; offset <= radius; offset++) {
          let idx = center + offset;
          // wrap around using modulo to handle clones
          if (slides.length > 0) {
            idx = ((idx % slides.length) + slides.length) % slides.length;
          }
          const s = slides[idx];
          if (!s) continue;
          const v = s.querySelector('video');
          if (!v) continue;
          const ds = v.getAttribute('data-src');
          if (ds && !v._lazyLoaded) {
            v.src = ds;
            try { v.load(); } catch (e) {}
            v._lazyLoaded = true;
            try { v.setAttribute('preload', 'auto'); } catch (e) {}
          }
        }
      } catch (e) {}
    }

    function onFirstInteraction() {
      preloadNearbyVideos(PRELOAD_RADIUS);
      // remove listeners
      try {
        swiperContainer.removeEventListener('pointerdown', onFirstInteraction);
        swiperContainer.removeEventListener('touchstart', onFirstInteraction);
        swiperContainer.removeEventListener('mouseenter', onFirstInteraction);
        if (prev) prev.removeEventListener('click', onFirstInteraction);
        if (next) next.removeEventListener('click', onFirstInteraction);
        if (sharedOverlay) sharedOverlay.removeEventListener('pointerdown', onFirstInteraction);
      } catch (e) {}
    }

    try {
      swiperContainer.addEventListener('pointerdown', onFirstInteraction, {passive:true});
      swiperContainer.addEventListener('touchstart', onFirstInteraction, {passive:true});
      swiperContainer.addEventListener('mouseenter', onFirstInteraction);
      if (prev) prev.addEventListener('click', onFirstInteraction);
      if (next) next.addEventListener('click', onFirstInteraction);
      if (sharedOverlay) sharedOverlay.addEventListener('pointerdown', onFirstInteraction, {passive:true});
    } catch (e) {}

    const baseSlideCount = Math.max(1, originalSlides.length || 1);
    const initialSlide = (() => {
      if (isFirstPageLoad) return 0;

      const randomRealIndex = Math.floor(Math.random() * baseSlideCount);
      const slidesPerViewByBreakpoint = (window.matchMedia && window.matchMedia('(max-width: 900px)').matches) ? 3 : 5;
      const centerOffset = Math.floor(slidesPerViewByBreakpoint / 2);
      return ((randomRealIndex - centerOffset) % baseSlideCount + baseSlideCount) % baseSlideCount;
    })();

    swiper = new Swiper(swiperContainer, {
      loop: true,
      initialSlide: initialSlide,
      centeredSlides: false,
      slidesPerView: 5,
      slidesPerGroup: 1,
      spaceBetween: 10,
      speed: 220,
      threshold: 20,
      resistanceRatio: 0,
      longSwipes: false,
      longSwipesRatio: 0.15,
      followFinger: false,
      shortSwipes: true,
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      breakpoints: {
        0: {
          slidesPerView: 3,
        },
        901: {
          slidesPerView: 5,
        }
      },
      on: {
        init: function () {
          update(this);
          try { syncTitleHeights(); } catch (e) {}
        },
        slideChangeTransitionStart: function () {
          try {
            // remove active class to avoid CSS scale artifacts on outgoing slide
            const all = swiperContainer.querySelectorAll('.swiper-slide');
            Array.prototype.forEach.call(all, s => s.classList.remove('active'));
            if (sharedOverlay) {
              // hide overlay during transition to prevent visual leftovers
              try { sharedOverlay.style.display = 'none'; } catch(e) {}
            }
          } catch (e) {}
          pauseInactiveVideos(this);
        },
        slideChangeTransitionEnd: function () {
          try {
            if (sharedOverlay) sharedOverlay.style.display = '';
          } catch(e) {}
          update(this);
        },
        resize: function () {
          update(this);
          try { syncTitleHeights(); } catch (e) {}
        }
      }
    });

    // Immediate single-slide switch on first swipe movement
    (function immediateSwipeOnMove() {
      let startX = null;
      let touched = false;
      let moved = false;
      const THRESHOLD = 10; // px to detect swipe start

      const getX = (e) => {
        if (!e) return 0;
        if (e.changedTouches && e.changedTouches[0]) return e.changedTouches[0].clientX;
        if (e.touches && e.touches[0]) return e.touches[0].clientX;
        return e.clientX;
      };

      const onDown = (e) => {
        touched = true;
        moved = false;
        startX = getX(e);
        try { if (swiper) swiper.allowTouchMove = true; } catch (e) {}
      };

      const onMove = (e) => {
        if (!touched || moved || startX === null) return;
        const x = getX(e);
        const delta = x - startX;
        if (Math.abs(delta) < THRESHOLD) return;
        moved = true;
        // left swipe -> next, right swipe -> prev
        try {
          chainGeneration++; cancelActiveChain();
          if (delta < 0) swiper.slideNext();
          else swiper.slidePrev();
        } catch (err) {}
        // prevent further native movement until pointerup
        try { if (swiper) swiper.allowTouchMove = false; } catch (e) {}
        // prevent default to avoid native touch behavior
        try { e.preventDefault && e.preventDefault(); } catch (e) {}
      };

      const onUp = () => {
        touched = false;
        moved = false;
        startX = null;
        try { if (swiper) swiper.allowTouchMove = true; } catch (e) {}
      };

      swiperContainer.addEventListener('pointerdown', onDown);
      swiperContainer.addEventListener('pointermove', onMove, {passive:false});
      window.addEventListener('pointerup', onUp);
      swiperContainer.addEventListener('touchstart', onDown, {passive:true});
      swiperContainer.addEventListener('touchmove', onMove, {passive:false});
      window.addEventListener('touchend', onUp);
    })();

    update(swiper);
  }

  document.querySelectorAll('.video-carousel').forEach(initCarousel);
});
